import { answerModel, type Config } from "../state/config";
import { chatStream, embedRequest, reasoningParams, RcpError } from "./rcpClient";

// Re-export so existing call sites that catch RcpError keep working without
// having to change their imports.
export { RcpError };

/** One part of a multimodal message content array (OpenAI-compatible). RCP
 *  serves these models via vLLM which speaks the same `image_url` schema. */
export type ContentPart =
  | { type: "text"; text: string }
  | { type: "image_url"; image_url: { url: string } };

export interface ChatMessage {
  role: "system" | "user" | "assistant";
  // A plain string for text-only turns, or an array of parts when the turn
  // carries images. rcpClient serializes this opaquely, so both shapes pass
  // straight through to RCP.
  content: string | ContentPart[];
}

// Match the server-side batch size that worked well in load tests; large enough
// to amortize the round-trip, small enough that one slow request doesn't stall
// a whole document's ingestion.
const EMBED_BATCH_SIZE = 32;

/** Embeds a list of texts (chunks of a document), batched. Routed through
 *  rcpClient — direct to RCP if the user has a personal key, otherwise via
 *  the server proxy using the shared app key.
 *
 *  `onBatchDone(done)` is called after each batch with the cumulative number
 *  of vectors produced so far (≤ texts.length). Useful for surfacing progress
 *  to the UI on large documents (200+ chunks). */
export async function embedTexts(
  texts: string[],
  cfg: Config,
  signal?: AbortSignal,
  onBatchDone?: (done: number) => void,
): Promise<Float32Array[]> {
  if (texts.length === 0) return [];
  const out: Float32Array[] = [];
  for (let i = 0; i < texts.length; i += EMBED_BATCH_SIZE) {
    const batch = texts.slice(i, i + EMBED_BATCH_SIZE);
    const data = await embedRequest(
      { model: cfg.embeddingModel, input: batch },
      cfg,
      signal,
    );
    if (data.data?.length !== batch.length) {
      throw new RcpError(
        500,
        `RCP /embeddings: expected ${batch.length} vectors, got ${data.data?.length ?? 0}`,
      );
    }
    for (const item of data.data) out.push(new Float32Array(item.embedding));
    onBatchDone?.(out.length);
  }
  return out;
}

/** Embeds a single short text (typically a user query). */
export async function embedQuery(
  text: string,
  cfg: Config,
  signal?: AbortSignal,
): Promise<Float32Array> {
  const [vec] = await embedTexts([text], cfg, signal);
  return vec;
}

/** A single SSE chunk from the chat endpoint. `content` is the user-facing
 *  answer; `thinking` is the reasoning trace emitted by reasoning models
 *  (Qwen3-Thinking, Kimi-K2.* Thinking, etc.) via `delta.reasoning_content`.
 *  Splitting them lets the UI surface "Réflexion…" separately from the final
 *  answer, and lets us tell prefill-silence apart from thinking-silence. */
export type ChatStreamDelta =
  | { kind: "content"; text: string }
  | { kind: "thinking"; text: string };

/** Streams assistant token deltas from the chat endpoint. The SSE parser is
 *  the same regardless of whether the underlying transport is direct RCP or
 *  the server proxy — both speak the OpenAI-compatible event format. */
export async function* streamChatCompletion(
  messages: ChatMessage[],
  cfg: Config,
  signal?: AbortSignal,
): AsyncGenerator<ChatStreamDelta, void, void> {
  // La réponse finale n'envoie toujours AUCUN `max_tokens` (cf. app/main.py :
  // un plancher imposé à distance avait cassé la prod le 03.09). Elle envoie en
  // revanche le réglage de l'interrupteur Réflexion : sur GLM-5.3-Flash,
  // `reasoning_effort: "low"` divise la sortie par sept sans changer la note en
  // lecture de document. Les autres modèles ne reçoivent rien et gardent leur
  // réflexion dans `reasoning_content`, que l'UI affiche comme « Réflexion… ».
  const res = await chatStream(
    {
      model: answerModel(cfg),
      messages,
      ...reasoningParams(
        answerModel(cfg),
        cfg.thinkingEnabled ? "libre" : "bas",
      ),
    },
    cfg,
    signal,
  );

  const reader = res.body!.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });

    const lines = buffer.split("\n");
    buffer = lines.pop() ?? "";

    for (const rawLine of lines) {
      const line = rawLine.trim();
      if (!line || !line.startsWith("data:")) continue;
      const payload = line.slice(5).trim();
      if (payload === "[DONE]") return;
      try {
        const parsed = JSON.parse(payload) as {
          choices?: Array<{
            delta?: { content?: string; reasoning_content?: string };
          }>;
        };
        const delta = parsed.choices?.[0]?.delta;
        const thinking = delta?.reasoning_content;
        if (thinking) yield { kind: "thinking", text: thinking };
        const content = delta?.content;
        if (content) yield { kind: "content", text: content };
      } catch {
        // incomplete JSON chunk boundary — skip
      }
    }
  }
}
